import { useEffect, useRef, useState } from "react";
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface ISiteMaintenanceBanner {
    isEnabled: boolean;
    bannerMessage: string;
    bannerBackgroundColor: string;
    bannerTextColor: string;
    bannerFontSize: string;
    bannerFontWeight: string;
    bannerTextAlign: string;
    bannerPadding: string;
    startDateTime: string;
    endDateTime: string;
    selectedTimezone: string;
    disableButtons: boolean;
    disableNavLinks: boolean;
    disabledTooltip: string;
}

export const IS_ENABLED = false;
export const BANNER_MESSAGE = "This site is currently undergoing scheduled maintenance. Please try again later.";
export const BANNER_BACKGROUND_COLOR = "#f8d7da";
export const BANNER_TEXT_COLOR = "#721c24";
export const START_DATE_TIME = "";
export const END_DATE_TIME = "";
export const SELECTED_TIMEZONE = "America/Chicago";
export const BANNER_FONT_SIZE = "15px";
export const BANNER_FONT_WEIGHT = "500";
export const BANNER_TEXT_ALIGN = "center";
export const BANNER_PADDING = "12px 24px";
export const DISABLE_BUTTONS = true;
export const DISABLE_NAV_LINKS = true;
export const DISABLED_TOOLTIP = "Payments are temporarily unavailable during maintenance.";

export const TIMEZONE_OPTIONS = [
    { label: "Eastern (ET)", value: "America/New_York" },
    { label: "Central (CT)", value: "America/Chicago" },
    { label: "Mountain (MT)", value: "America/Denver" },
    { label: "Pacific (PT)", value: "America/Los_Angeles" },
    { label: "Alaska (AKT)", value: "America/Anchorage" },
    { label: "Hawaii (HT)", value: "Pacific/Honolulu" },
    { label: "UTC", value: "UTC" },
];

export const coerceBool =(value: boolean | string | undefined, fallback: boolean): boolean => {
    if (value === undefined || value === null) return fallback;
    if (typeof value === "boolean") return value;
    return value === "true";
}

const DISABLED_ATTR = "data-maintenance-disabled";

const preventClick = (e: Event) => {
    e.preventDefault();
    e.stopImmediatePropagation();
}

const applyDisabled = (disableButtons: boolean, disableNavLinks: boolean, tooltip: string) => {
    if (disableButtons) {
        document.querySelectorAll<HTMLButtonElement | HTMLAnchorElement>(
            "button:not([data-maintenance-ignore]), a.btn:not([data-maintenance-ignore])"
        ).forEach((el) => {
            el.setAttribute(DISABLED_ATTR, "true");
            el.setAttribute("title", tooltip);
            (el as HTMLButtonElement).disabled = true;
            el.style.pointerEvents = "none";
            el.style.opacity = "0.5";
            el.style.cursor = "not-allowed";
            el.addEventListener("click", preventClick, true);
        });
    }
    if (disableNavLinks) {
        document.querySelectorAll<HTMLAnchorElement>(
            "a[href]:not(.btn):not([data-maintenance-ignore])"
        ).forEach((el) => {
            el.setAttribute(DISABLED_ATTR, "true");
            el.setAttribute("title", tooltip);
            el.setAttribute("aria-disabled", "true");
            el.style.pointerEvents = "none";
            el.style.opacity = "0.5";
            el.style.cursor = "not-allowed";
            el.addEventListener("click", preventClick, true);
        });
    }
}

const removeDisabled = () => {
    document.querySelectorAll<HTMLElement>(`[${DISABLED_ATTR}]`).forEach((el) => {
        el.removeAttribute(DISABLED_ATTR);
        el.removeAttribute("title");
        el.removeAttribute("aria-disabled");
        (el as HTMLButtonElement).disabled = false;
        el.style.pointerEvents = "";
        el.style.opacity = "";
        el.style.cursor = "";
        el.removeEventListener("click", preventClick, true);
    });
}

const localStringToUTC = (localStr: string, timezone: string): Date => {
    const roughUTC = new Date(localStr + "Z");
    const offsetMs = getUTCOffsetMs(roughUTC, timezone);
    return new Date(roughUTC.getTime() - offsetMs);
}

const getUTCOffsetMs = (date: Date, timezone: string): number => {
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
        hour12: false,
    }).formatToParts(date);

    const get = (type: string) => parseInt(parts.find(p => p.type === type)?.value ?? "0", 10);
    const localDate = new Date(Date.UTC(
        get("year"), get("month") - 1, get("day"),
        get("hour") % 24, get("minute"), get("second")
    ));
    return localDate.getTime() - date.getTime();
}

const formatInTimezone = (localStr: string, timezone: string): string => {
    if (!localStr) return "";
    const utc = localStringToUTC(localStr, timezone);
    return utc.toLocaleString("en-US", {
        timeZone: timezone,
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
    });
}

const SiteMaintenanceBanner = ({
    isEnabled,
    bannerMessage,
    bannerBackgroundColor,
    bannerTextColor,
    bannerFontSize,
    bannerFontWeight,
    bannerTextAlign,
    bannerPadding,
    startDateTime,
    endDateTime,
    selectedTimezone,
    disableButtons,
    disableNavLinks,
    disabledTooltip,
}: ISiteMaintenanceBanner) => {
    const _isEnabled = coerceBool(isEnabled, IS_ENABLED);
    const _disableButtons = coerceBool(disableButtons, DISABLE_BUTTONS);
    const _disableNavLinks = coerceBool(disableNavLinks, DISABLE_NAV_LINKS);

    const [isActive, setIsActive] = useState<boolean>(false);
    const observerRef = useRef<MutationObserver | null>(null);

    useEffect(() => {
        const evaluate = () => {
            const hasSchedule = !!startDateTime && !!endDateTime;
            if (hasSchedule) {
                const now = new Date();
                const start = localStringToUTC(startDateTime, selectedTimezone);
                const end = localStringToUTC(endDateTime, selectedTimezone);
                setIsActive(now >= start && now <= end);
            } else {
                setIsActive(_isEnabled);
            }
        };

        evaluate();
        const interval = setInterval(evaluate, 30_000);
        return () => clearInterval(interval);
    }, [_isEnabled, startDateTime, endDateTime, selectedTimezone]);

    useEffect(() => {
        observerRef.current?.disconnect();

        if (!isActive) {
            removeDisabled();
            return;
        }

        applyDisabled(_disableButtons, _disableNavLinks, disabledTooltip);

        observerRef.current = new MutationObserver(() => {
            applyDisabled(_disableButtons, _disableNavLinks, disabledTooltip);
        });
        observerRef.current.observe(document.body, { childList: true, subtree: true });

        return () => {
            observerRef.current?.disconnect();
            removeDisabled();
        };
    }, [isActive, _disableButtons, _disableNavLinks, disabledTooltip]);

    if (!isActive) return null;

    return (
        <div
            role="alert"
            aria-live="assertive"
            style={{
                backgroundColor: bannerBackgroundColor || BANNER_BACKGROUND_COLOR,
                color: bannerTextColor || BANNER_TEXT_COLOR,
                padding: bannerPadding || BANNER_PADDING,
                textAlign: (bannerTextAlign || BANNER_TEXT_ALIGN) as any,
                fontSize: bannerFontSize || BANNER_FONT_SIZE,
                fontWeight: (bannerFontWeight || BANNER_FONT_WEIGHT) as any,
                position: "sticky",
                top: 0,
                zIndex: 9999,
                borderBottom: `1px solid ${(bannerTextColor || BANNER_TEXT_COLOR)}33`,
                lineHeight: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: (bannerTextAlign || BANNER_TEXT_ALIGN) === "center" ? "center" : (bannerTextAlign || BANNER_TEXT_ALIGN) === "right" ? "flex-end" : "flex-start",
                flexWrap: "wrap" as const,
            }}
        >
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ color: "#FFC107", fontSize: "18px", marginRight: "10px", flexShrink: 0 }} />
            {bannerMessage || BANNER_MESSAGE}
        </div>
    );
};

export default SiteMaintenanceBanner;